import React, { useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import Table, { ServerSort } from "../../components/common/Table";
import {
  EyeIcon,
  SignalIcon,
  SignalSlashIcon,
  Squares2X2Icon,
  DeviceTabletIcon,
} from "@heroicons/react/24/outline";
import {
  Screen,
  ScreenColumnFilter,
  ScreenFilterField,
} from "../../schemas/screenSchema";
import { useScreensInfinite } from "../../hooks/useScreensInfinite";
import ScreenDetailsModal from "../../components/modals/ScreenDetailsModal";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { useScreenStatusSubscription } from "../../hooks/useScreenStatusSubscription";
import { formatLastOnDate, formatLastOnTime } from "../../utils/dateUtils";

const STATUS_TABS: {
  label: string;
  value: "all" | "on" | "off";
  icon: React.ReactNode;
}[] = [
  {
    label: "All",
    value: "all",
    icon: <Squares2X2Icon className="h-5 w-5 mr-1" />,
  },
  {
    label: "On",
    value: "on",
    icon: <SignalIcon className="h-5 w-5 mr-1" />,
  },
  {
    label: "Off",
    value: "off",
    icon: <SignalSlashIcon className="h-5 w-5 mr-1" />,
  },
];

const ORIENTATION_TABS: {
  label: string;
  value: "" | "landscape" | "portrait";
  icon: React.ReactNode;
}[] = [
  {
    label: "All",
    value: "",
    icon: <Squares2X2Icon className="h-5 w-5" />,
  },
  {
    label: "Landscape",
    value: "landscape",
    icon: <DeviceTabletIcon className="h-5 w-5 rotate-90" />,
  },
  {
    label: "Portrait",
    value: "portrait",
    icon: <DeviceTabletIcon className="h-5 w-5" />,
  },
];

const ScreenList: React.FC = () => {
  useScreenStatusSubscription();
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [selectedScreenId, setSelectedScreenId] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<"all" | "on" | "off">("all");
  const [orientation, setOrientation] = useState<"" | "landscape" | "portrait">(
    "",
  );
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {},
  );
  // Sorted server-side: the list is paged, so sorting only the loaded rows
  // would leave matching screens buried on pages that haven't loaded yet.
  const [sort, setSort] = useState<ServerSort | null>(null);

  const statusFilter =
    statusTab === "on" ? true : statusTab === "off" ? false : undefined;

  const columnFilterArray = useMemo<ScreenColumnFilter[]>(
    () =>
      Object.entries(columnFilters)
        .filter(([, value]) => value && value.trim())
        .map(([field, value]) => ({
          field: field as ScreenFilterField,
          value: value.trim(),
        })),
    [columnFilters],
  );

  const {
    screens,
    isLoading: isScreensLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useScreensInfinite({
    tagType: "system_active",
    status: statusFilter,
    orientation: orientation || undefined,
    columnFilters: columnFilterArray.length ? columnFilterArray : undefined,
    sortBy: sort ? (sort.key as ScreenFilterField) : undefined,
    sortOrder: sort ? (sort.desc ? "DESC" : "ASC") : undefined,
  });

  // The status filter reaches the server either as the `status` boolean (tab)
  // or as the "on"/"off" STATUS column filter, so resolve both to one value.
  const activeStatusFilter = useMemo<boolean | undefined>(() => {
    if (statusFilter !== undefined) return statusFilter;
    const raw = columnFilters.STATUS?.trim().toLowerCase();
    if (raw === "on") return true;
    if (raw === "off") return false;
    return undefined;
  }, [statusFilter, columnFilters.STATUS]);

  /**
   * Screens whose live status no longer satisfies the active status filter.
   *
   * Filtering happens server-side and is part of the query key, but
   * `useScreenStatusSubscription` patches `status` straight into the cache (by
   * design — a refetch per event churns the whole infinite list). A screen that
   * flips after the fetch therefore lingers in a result set it no longer
   * belongs to. Surface that rather than dropping the row: client-side removal
   * would desync the server's page offsets and yank rows out from under the
   * user mid-click.
   */
  const driftedIds = useMemo(() => {
    if (activeStatusFilter === undefined) return new Set<string>();
    return new Set(
      screens
        .filter((screen) => screen.status !== activeStatusFilter)
        .map((screen) => screen.id),
    );
  }, [screens, activeStatusFilter]);

  const { canView } = useAuthPermissions("screens");
  const selectedScreen =
    screens.find((screen) => screen.id === selectedScreenId) || null;

  const handleView = (screen: Screen) => {
    setSelectedScreenId(screen.id);
    setIsDeviceModalOpen(true);
  };

  const columns: ColumnDef<Screen>[] = [
    {
      accessorKey: "screenName",
      header: "Screen",
      meta: { filterKey: "SCREEN_NAME", sortKey: "SCREEN_NAME" },
    },
    {
      accessorKey: "restaurant.code",
      header: "Customer",
      meta: { filterKey: "CUSTOMER_ID", sortKey: "CUSTOMER_ID" },
    },
    {
      accessorKey: "restaurant.name",
      header: "Restaurant",
      meta: { filterKey: "RESTAURANT_NAME", sortKey: "RESTAURANT_NAME" },
    },
    {
      accessorKey: "region.city.name",
      header: "City",
      meta: { filterKey: "CITY", sortKey: "CITY" },
    },
    {
      accessorKey: "region.name",
      header: "Region",
      meta: { filterKey: "REGION", sortKey: "REGION" },
    },
    {
      accessorKey: "orientation",
      header: "Orientation",
      meta: { filterKey: "ORIENTATION" },
      enableSorting: false,
    },
    {
      id: "status",
      header: "Status",
      meta: { filterKey: "STATUS", filterPlaceholder: "on / off" },
      cell: ({ row }) => {
        const screen = row.original;
        if (screen.status === null || screen.status === undefined) {
          return <span className="text-sm text-gray-500">-</span>;
        }
        const isOn = screen.status === true;
        const hasDrifted = driftedIds.has(screen.id);
        return (
          <span
            title={
              hasDrifted
                ? "Changed after this list was loaded — refresh to re-apply the filter"
                : undefined
            }
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isOn ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
            } ${hasDrifted ? "ring-1 ring-amber-400" : ""}`}
          >
            {isOn ? "On" : "Off"}
          </span>
        );
      },
    },
    {
      id: "lastOnDate",
      header: "Last On Date",
      accessorKey: "lastOnAt",
      enableSorting: true,
      meta: {
        filterKey: "LAST_ON_DATE",
        filterPlaceholder: "dd-mm-yyyy",
        sortKey: "LAST_ON_DATE",
      },
      cell: ({ row }) => {
        const screen = row.original;
        const dateTime = screen.lastOnAt || new Date().toISOString();
        return (
          <span className="text-sm text-gray-700">
            {formatLastOnDate(dateTime)}
          </span>
        );
      },
    },
    {
      id: "lastOnTime",
      header: "Last On Time",
      accessorKey: "lastOnAt",
      enableSorting: true,
      meta: {
        filterKey: "LAST_ON_TIME",
        filterPlaceholder: "hh:mm",
        sortKey: "LAST_ON_TIME",
      },
      cell: ({ row }) => {
        const screen = row.original;
        const dateTime = screen.lastOnAt || new Date().toISOString();
        return (
          <span className="text-sm text-gray-700">
            {formatLastOnTime(dateTime)}
          </span>
        );
      },
    },
    // {
    //   accessorKey: "verificationCode",
    //   header: "Device Code",
    //   meta: { filterKey: "DEVICE_CODE" },
    //   enableSorting: false,
    // },
  ];

  const actions = [
    {
      label: "View",
      icon: <EyeIcon className="h-4 w-4" />,
      onClick: handleView,
      variant: "primary" as const,
    },
  ];

  return (
    <div className="flex flex-col px-6 py-2">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Screens</h1>
      </div>

      {driftedIds.size > 0 && (
        <div className="mb-3 flex items-center justify-between gap-4 rounded border border-amber-200 bg-amber-50 px-4 py-2">
          <span className="text-sm text-amber-800">
            {driftedIds.size} screen{driftedIds.size > 1 ? "s" : ""} changed
            status and no longer match{driftedIds.size > 1 ? "" : "es"} this
            filter.
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            className="shrink-0 text-sm font-medium text-amber-900 underline hover:text-amber-950"
          >
            Refresh list
          </button>
        </div>
      )}

      {canView && (
        <Table
          data={screens as Screen[]}
          columns={columns}
          actions={actions}
          searchable={false}
          pagination={true}
          isLoading={isScreensLoading}
          maxHeight="75vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          columnFiltersEnabled
          onColumnFilterChange={setColumnFilters}
          onSortChange={setSort}
          // filterSlot={
          //   <>
          //     {STATUS_TABS.map((tab) => {
          //       const isActive = statusTab === tab.value;
          //       return (
          //         <button
          //           key={tab.value}
          //           onClick={() => setStatusTab(tab.value)}
          //           className={`p-2 mx-1 rounded border font-medium flex items-center transition-all duration-150 ${
          //             isActive
          //               ? "bg-secondary-default text-white"
          //               : "bg-white text-secondary-default border-secondary-default hover:bg-secondary-light hover:text-white"
          //           }`}
          //         >
          //           {tab.icon}
          //           <span>{tab.label}</span>
          //         </button>
          //       );
          //     })}
          //     <span className="mx-1 h-6 w-px bg-gray-300" />
          //     {ORIENTATION_TABS.map((tab) => {
          //       const isActive = orientation === tab.value;
          //       return (
          //         <button
          //           key={tab.value || "all"}
          //           onClick={() => setOrientation(tab.value)}
          //           title={tab.label}
          //           className={`p-2 mx-1 rounded border font-medium flex items-center transition-all duration-150 ${
          //             isActive
          //               ? "bg-secondary-default text-white"
          //               : "bg-white text-secondary-default border-secondary-default hover:bg-secondary-light hover:text-white"
          //           }`}
          //         >
          //           {tab.icon}
          //         </button>
          //       );
          //     })}
          //   </>
          // }
        />
      )}

      {isDeviceModalOpen && (
        <ScreenDetailsModal
          isOpen={isDeviceModalOpen}
          onClose={() => {
            setIsDeviceModalOpen(false);
            setSelectedScreenId(null);
          }}
          device={selectedScreen}
        />
      )}
    </div>
  );
};

export default ScreenList;
